import { untracked } from '@angular/core';
import type { DataGridState } from './contracts/data-grid-state.interface';
import type { GridStatePersistence } from './contracts/grid-state-persistence.interface';

/** Layout fields owned by `d-data-grid` (not filters or panel chrome). */
export interface GridLayoutState {
  /** Ordered group fields. Empty means ungrouped. */
  readonly groupByFields: Array<string>;
  /** Column fields hidden by the user. */
  readonly hiddenColumns: Array<string>;
  /** User-defined shared column order. */
  readonly columnOrder: Array<string>;
  /** User-defined discriminator column order, keyed by scope. */
  readonly discriminatorColumnOrder: Record<string, Array<string>>;
  /** User-defined column widths in pixels, keyed by field. */
  readonly columnWidths: Record<string, number>;
}

/**
 * Merges overrides onto the current persisted state.
 * Preserves orchestration fields such as `columnFilters` and panel `infoBannerDismissed`
 * unless those fields are included in `overrides`.
 *
 * @param current - Current persisted grid state.
 * @param overrides - Fields to overwrite (layout and/or panel chrome).
 * @returns A full state object safe to pass to `save()`.
 */
export function mergeGridLayoutState(
  current: DataGridState,
  overrides: Partial<DataGridState>
): DataGridState {
  return {
    ...current,
    ...overrides
  };
}

/**
 * Writes layout overrides through the persistence port without dropping other state.
 * Reads the current snapshot untracked so effects can persist without looping on `save()`.
 *
 * @param persistence - Host-provided persistence port.
 * @param layout - Layout fields to overwrite.
 */
export function saveGridLayout(persistence: GridStatePersistence, layout: Partial<GridLayoutState>): void {
  untracked((): void => {
    persistence.save(mergeGridLayoutState(persistence.state(), layout));
  });
}

/**
 * Returns true when any layout field has a user-persisted value.
 *
 * @param state - Persisted grid state.
 * @returns True when at least one layout field is non-empty.
 */
export function hasPersistedGridLayout(state: DataGridState): boolean {
  return (
    state.groupByFields.length > 0 ||
    state.hiddenColumns.length > 0 ||
    state.columnOrder.length > 0 ||
    Object.keys(state.discriminatorColumnOrder).length > 0 ||
    Object.keys(state.columnWidths).length > 0
  );
}
